export interface CookieOptions {
  expires?: number | Date; // duração (s) ou data absoluta
  path?: string;
  domain?: string;
  secure?: boolean;
  sameSite?: "lax" | "strict" | "none";
}

export class Cookies {
  /**
   * Cria ou atualiza um cookie.
   * Aceita strings, números, booleanos ou objetos (que são serializados em JSON).
   */
  static set(name: string, value: unknown, options: CookieOptions = {}): void {
    let stringValue: string;

    try {
      if (typeof value === "object" && value !== null) {
        stringValue = JSON.stringify(value);
      } else {
        stringValue = String(value);
      }
    } catch {
      throw new Error("Falha ao serializar valor para cookie.");
    }

    let cookie = `${encodeURIComponent(name)}=${encodeURIComponent(
      stringValue
    )}`;

    // Expiração
    if (options.expires !== undefined) {
      const expires =
        options.expires instanceof Date
          ? options.expires.toUTCString()
          : new Date(Date.now() + options.expires * 1000).toUTCString();
      cookie += `; expires=${expires}`;
    }

    // Outras opções
    cookie += `; path=${options.path ?? "/"}`;
    if (options.domain) cookie += `; domain=${options.domain}`;
    if (options.secure) cookie += `; Secure`;
    if (options.sameSite) cookie += `; SameSite=${options.sameSite}`;

    document.cookie = cookie;
  }

  /**
   * Lê um cookie. Se for JSON válido, retorna o objeto original.
   */
  static get<T = string>(name: string): T | string | undefined {
    const cookies = document.cookie
      .split("; ")
      .filter(Boolean)
      .map((c) => c.split("="))
      .reduce<Record<string, string>>((acc, [key, val]) => {
        acc[decodeURIComponent(key)] = decodeURIComponent(val ?? "");
        return acc;
      }, {});

    const raw = cookies[name];
    if (!raw) return undefined;

    try {
      return JSON.parse(raw) as T; // tenta desserializar
    } catch {
      return raw; // se falhar, retorna string bruta
    }
  }

  /**
   * Remove um cookie.
   */
  static delete(name: string, options: CookieOptions = {}): void {
    Cookies.set(name, "", { ...options, expires: -1 });
  }

  /**
   * Retorna todos os cookies como objeto.
   * Tenta desserializar automaticamente valores em JSON.
   */
  static list(): Record<string, any> {
    return document.cookie
      .split("; ")
      .filter(Boolean)
      .map((c) => c.split("="))
      .reduce<Record<string, unknown>>((acc, [key, val]) => {
        const decodedKey = decodeURIComponent(key);
        const decodedVal = decodeURIComponent(val ?? "");
        try {
          acc[decodedKey] = JSON.parse(decodedVal);
        } catch {
          acc[decodedKey] = decodedVal;
        }
        return acc;
      }, {});
  }
}
